import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
} from "react";
import { FormattedMessage } from "react-intl";
import {
  Biosimilar,
  DrugCostInfo,
  DrugDosageInfo,
  DrugFootnote,
  DrugInfo,
  PrescriptionDrug,
} from "../../../@types";
import { getDrugDosages } from "../../../api";
import {
  AnalyticsActionType,
  Ga4Event,
  Ga4EventType,
} from "../../../app/contexts/Analytics/types";
import { AppStore } from "../../../app/store";
import {
  EmptyTableField,
  FootnoteMarker,
  RenderedTable,
  TooltipWithClose,
} from "../../../components";
import { displayDrug, useTableConfig, useTranslate } from "../../../helpers";
import {
  InterchangeableBiologicsHelpDrawer,
  OtherDrugInfoFootnotes,
} from "./components";
import { OtherDrugInfoFoonoteType } from "./components/OtherDrugInfoFootnotes";
import { getCoveredIBs } from "./helpers";

export const OtherDrugInformationTable: FunctionComponent<{
  drugCostInfo: DrugCostInfo;
  drugInfo: DrugInfo[];
  prescriptions: PrescriptionDrug[];
  isOpen?: boolean;
}> = ({ drugCostInfo, drugInfo, prescriptions, isOpen = false }) => {
  const t = useTranslate();
  const {
    state: { language },
    dispatch,
  } = useContext(AppStore.AppContext);
  const [footnotes, setFootnotes] = useState<OtherDrugInfoFoonoteType[]>([]);
  const [ibsWithNames, setIbsWithNames] = useState<
    Record<string, Biosimilar[]>
  >({});
  const [ndcFootnoteHashMap, setNdcFootnoteHashMap] = useState({});
  const [ibImpressionRecorded, setIbImpressionRecorded] = useState(false);

  const removeIb = (ndc: string) =>
    setIbsWithNames(previousIbsWithNames => {
      const copy = { ...previousIbsWithNames };
      delete copy[ndc];
      return copy;
    });

  const matchNote = (textToMatch: string) => (note: OtherDrugInfoFoonoteType) =>
    note.text === textToMatch;

  useEffect(() => {
    const { restrictions } = drugCostInfo;

    const conditions = restrictions.filter(r => !!r.conditions.length);
    const opioids = restrictions.filter(r => r.opioid);
    const homeInfusions = restrictions.filter(r => r.home_infusion);
    const limitedDistros = restrictions.filter(r => r.limited_distribution);
    const vaccines = restrictions.filter(r => r.vaccine);

    const newNdcFootnoteHashMap = {};
    const notes: {
      text: string;
      type: DrugFootnote;
    }[] = [];

    conditions.forEach(c => {
      const conditionText =
        t("plan_details.drug_restrictions") + " " + c.conditions.join("; ");
      notes.push({
        text: conditionText,
        type: DrugFootnote.CONDITION,
      });
      const matchedIndex = notes.findIndex(matchNote(conditionText));
      newNdcFootnoteHashMap[c.ndc]
        ? newNdcFootnoteHashMap[c.ndc].push(matchedIndex)
        : (newNdcFootnoteHashMap[c.ndc] = [matchedIndex]);
    });

    if (opioids.length) {
      const opioidText = t("plan_details.opioid_footnote");
      notes.push({
        text: opioidText,
        type: DrugFootnote.OPIOID,
      });
      const matchedIndex = notes.findIndex(matchNote(opioidText));
      opioids.forEach(opiod => {
        newNdcFootnoteHashMap[opiod.ndc]
          ? newNdcFootnoteHashMap[opiod.ndc].push(matchedIndex)
          : (newNdcFootnoteHashMap[opiod.ndc] = [matchedIndex]);
      });
    }

    if (homeInfusions.length) {
      const homeInfusionText = t("drug_footnote.home_infusion");
      notes.push({
        text: homeInfusionText,
        type: DrugFootnote.HOME_INFUSION,
      });
      const matchedIndex = notes.findIndex(matchNote(homeInfusionText));
      homeInfusions.forEach(homeInfusion => {
        newNdcFootnoteHashMap[homeInfusion.ndc]
          ? newNdcFootnoteHashMap[homeInfusion.ndc].push(matchedIndex)
          : (newNdcFootnoteHashMap[homeInfusion.ndc] = [matchedIndex]);
      });
    }

    if (limitedDistros.length) {
      const limitedDistrosText = t("drug_footnote.limited_distribution");
      notes.push({
        text: limitedDistrosText,
        type: DrugFootnote.LIMITED_DISTRIBUTION,
      });
      const matchedIndex = notes.findIndex(matchNote(limitedDistrosText));
      limitedDistros.forEach(limitedDistro => {
        newNdcFootnoteHashMap[limitedDistro.ndc]
          ? newNdcFootnoteHashMap[limitedDistro.ndc].push(matchedIndex)
          : (newNdcFootnoteHashMap[limitedDistro.ndc] = [matchedIndex]);
      });
    }

    if (vaccines.length) {
      const vaccinesText = t("drug_footnote.vaccine");
      notes.push({
        text: vaccinesText,
        type: DrugFootnote.VACCINE,
      });
      const matchedIndex = notes.findIndex(matchNote(vaccinesText));
      vaccines.forEach(vaccine => {
        newNdcFootnoteHashMap[vaccine.ndc]
          ? newNdcFootnoteHashMap[vaccine.ndc].push(matchedIndex)
          : (newNdcFootnoteHashMap[vaccine.ndc] = [matchedIndex]);
      });
    }
    setNdcFootnoteHashMap(newNdcFootnoteHashMap);
    setFootnotes(notes);
  }, [drugCostInfo, language]);

  useEffect(() => {
    (async () => {
      if (!prescriptions) return;
      for (const prescription of prescriptions) {
        const drug = drugInfo.find(d => d.ndc === prescription.ndc);
        if (!drug) {
          removeIb(prescription.ndc);
          continue;
        }
        const biosimilars = drug.biosimilars;
        if (biosimilars.length === 0) {
          removeIb(prescription.ndc);
          continue;
        }
        const ibs = getCoveredIBs(biosimilars);
        if (ibs.length === 0) {
          removeIb(prescription.ndc);
          continue;
        }
        if (ibsWithNames[prescription.ndc]) {
          continue;
        }
        const existingIbs = Object.values(ibsWithNames).reduce(
          (acc, val) => acc.concat(val),
          []
        );
        const existingIbRxcuis = existingIbs.map(ib => ib.rxcui);
        const namedBiosimilars: Biosimilar[] = (await Promise.all(
          ibs.map(async biosimilar => {
            if (existingIbRxcuis.some(rxcui => rxcui === biosimilar.rxcui)) {
              return existingIbs.find(ib => ib.rxcui === biosimilar.rxcui);
            }
            let drugDosageInfo: DrugDosageInfo | undefined;
            try {
              drugDosageInfo = await getDrugDosages(biosimilar.rxcui);
            } catch (_e) {
              return;
              // No requirements around error handling. This is just a no-op
            }
            if (!drugDosageInfo) return;
            const matchingDosage = drugDosageInfo.dosages.find(
              dosage => dosage.rxcui === biosimilar.rxcui
            );
            if (!matchingDosage) return;
            const displayName = displayDrug(t, matchingDosage);
            return {
              ...biosimilar,
              displayName: displayName || biosimilar.name,
            };
          })
        ).then(biosimilars =>
          biosimilars.filter(biosimilar => !!biosimilar)
        )) as Biosimilar[];
        // This ^^ was flagged by TS as still containing undefined values,
        // but I cannot see how that would be true after the `filter`

        // Set in state
        setIbsWithNames(prevBiosimilarsWithNames => {
          const ibsWithNames = {
            ...prevBiosimilarsWithNames,
            [prescription.ndc]: namedBiosimilars,
          };
          return ibsWithNames;
        });
      }
    })();
  }, [prescriptions, drugInfo]);

  useEffect(() => {
    if (
      !isOpen ||
      Object.keys(ibsWithNames).length === 0 ||
      ibImpressionRecorded
    )
      return;
    // Analytics - IB impressions
    setIbImpressionRecorded(true);
    Object.entries(ibsWithNames).forEach(([ndc, ibs]) => {
      const prescription = prescriptions.find(rx => rx.ndc === ndc);
      if (!prescription) return;
      const mct_biosimilar_drug_name = ibs.map(ib => ib.displayName).join(", ");
      const mct_biosimilar_reference_drug_name = displayDrug(t, prescription);
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.BIOSIMILAR_IMPRESSION,
          event_type: Ga4EventType.NON_INTERACTION,
          mct_biosimilar_drug_name,
          mct_biosimilar_reference_drug_name,
        },
      });
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "biosimilar impression",
          event_label: mct_biosimilar_reference_drug_name,
        },
      });
    });
  }, [isOpen, ibsWithNames, prescriptions]);

  const drugData = prescriptions.reduce(
    (
      accumulator: Record<string, unknown>[],
      prescription: PrescriptionDrug
    ) => {
      const drug = drugInfo.find(d => d.ndc === prescription.ndc);
      if (!drug) {
        return accumulator;
      }
      const ibs = ibsWithNames[drug.ndc];
      const hasIbs = !!ibs && ibs.length > 0;
      const tooltipContent = (
        <div
          className="OtherDrugInfo__tooltipContent"
          id="quantity-limits-tooltip"
          role="tooltip"
        >
          <span className="ds-u-font-weight--bold">
            {t("plan_terms.quantity_limits_amount")}
          </span>

          <span className="ds-u-margin-bottom--1">
            {drug.quantity_limit ? drug.quantity_limit_amount : ""}
          </span>
          <span className="ds-u-font-weight--bold">
            {t("plan_terms.quantity_limits_frequency")}
          </span>
          <span>
            {drug.quantity_limit
              ? `${drug.quantity_limit_days} ${t("days")}`
              : ""}
          </span>
        </div>
      );
      const displayDrugString = displayDrug(t, prescription);
      accumulator.push({
        drugName: (
          <>
            <span className="mct-c-table__header-title">
              {displayDrugString}
              {ndcFootnoteHashMap[drug.ndc] &&
                ndcFootnoteHashMap[drug.ndc].map((footnoteIndex: number) => (
                  <FootnoteMarker
                    key={`footnoteIndex-${footnoteIndex}`}
                    id={`footnote-${footnoteIndex}`}
                    marker={(footnoteIndex + 1).toString()}
                  />
                ))}
            </span>
            {hasIbs && (
              <InterchangeableBiologicsHelpDrawer
                displayDrugString={displayDrugString}
                interchangeableBiologics={ibs}
              />
            )}
          </>
        ),
        tier:
          drug.tier > 0 ? (
            <FormattedMessage
              id="plan_terms.tier-number"
              tagName="span"
              values={{
                tier: drug.tier,
              }}
            />
          ) : (
            t("plan_terms.not_covered")
          ),
        priorAuthorization: drug.prior_auth ? (
          t("yes")
        ) : (
          <EmptyTableField withDash />
        ),
        quantityLimits: drug.quantity_limit ? (
          <TooltipWithClose title={tooltipContent} headerText="">
            {t("yes")}
          </TooltipWithClose>
        ) : (
          <EmptyTableField withDash />
        ),
        stepTherapy: drug.step_therapy ? (
          t("yes")
        ) : (
          <EmptyTableField withDash />
        ),
      });
      return accumulator;
    },
    []
  );

  const data = useTableConfig(drugData, [
    drugInfo,
    drugCostInfo,
    ndcFootnoteHashMap,
    footnotes,
    ibsWithNames,
    language,
  ]);

  const columns = useTableConfig(
    [
      {
        accessor: "drugName",
        Footer: <OtherDrugInfoFootnotes footnotes={footnotes} />,
      },
      {
        Header: t("plan_terms.tier"),
        accessor: "tier",
      },
      {
        Header: t("plan_terms.prior_authorization"),
        accessor: "priorAuthorization",
      },
      {
        Header: t("plan_terms.quantity_limits"),
        accessor: "quantityLimits",
      },
      {
        Header: t("plan_terms.step_therapy"),
        accessor: "stepTherapy",
      },
    ],
    [drugInfo, footnotes, language]
  );

  return (
    <RenderedTable
      id="OtherDrugInformation"
      tableColumns={columns}
      tableData={data}
      caption={t("plan_terms.other_drug_information")}
      className={footnotes.length ? "has-footnotes" : ""}
      borderless
      rowHeaders
      stackableBreakpoint="lg"
    />
  );
};
